import React, { useState, useEffect } from 'react';
import Testimonials from '../components/Testimonials'; // Your frontend component
import axios from "axios";
import config from '../config/config';

const TestimonialsAdmin = ({ slug = '', onSave, onClose }) => {
  const token = localStorage.getItem("token");

  // Default config
  const defaultConfig = {
    title: "What Our Customers Say",
    subtitle: "Don't just take our word for it. Here's what our happy customers have to say.",
    testimonials: [],
    devMode: false,
    customStyles: {
      container: {
        padding: 'py-12 md:py-20',
        background: 'bg-gray-50',
        titleColor: '#111827',
        subtitleColor: '#4B5563',
        textColor: '#374151',
        starColor: '#fbbf24',
        ctaBg: '#111827',
        ctaText: '#ffffff',
      },
    },
  };

  // States
  const [title, setTitle] = useState(defaultConfig.title);
  const [subtitle, setSubtitle] = useState(defaultConfig.subtitle);
  const [testimonials, setTestimonials] = useState(defaultConfig.testimonials);
  const [devMode, setDevMode] = useState(defaultConfig.devMode);
  const [container, setContainer] = useState(defaultConfig.customStyles.container);

  const [allTestimonials, setAllTestimonials] = useState([]); // All available testimonials from backend
  const [selectedTestimonialIds, setSelectedTestimonialIds] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullScreenPreview, setFullScreenPreview] = useState(false);

  // Load saved config
  useEffect(() => {
    const loadTestimonialsSettings = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        let response;
        const isDuplicate = slug.includes('-');

        if (isDuplicate) {
          response = await axios.get(
            `${config.API_URL}/api/pullCustomSettings/${slug}`,
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        } else {
          response = await axios.get(
            config.API_URL + '/api/pullTestimonialsSettings',
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        }

        const dbData = isDuplicate
          ? (response.data.customSettings || {})
          : (response.data.testimonialsSettings || {});

        setTitle(dbData.title || defaultConfig.title);
        setSubtitle(dbData.subtitle || defaultConfig.subtitle);
        setTestimonials(dbData.testimonials || []);
        setDevMode(dbData.devMode || defaultConfig.devMode);
        setContainer(dbData.customStyles?.container || defaultConfig.customStyles.container);
      } catch (error) {
        console.error("Failed to load testimonials settings:", error);
      } finally {
        setLoading(false);
      }
    };

    loadTestimonialsSettings();
  }, [token, slug]);

  // Fetch ALL available testimonials for the modal
  const fetchAllTestimonials = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/api/pullTestimonials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllTestimonials(response.data.testimonials || []);
    } catch (err) {
      console.error("Failed to fetch all testimonials:", err);
    }
  };

  useEffect(() => {
    if (token) fetchAllTestimonials();
  }, [token]);

  // Current config
  const currentConfig = {
    title,
    subtitle,
    testimonials,
    devMode,
    customStyles: { container },
  };

  // Save
  const saveTestimonialsSettings = async () => {
    try {
      setSaving(true);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const endpoint = slug ? `/api/saveTestimonialsSettings/${slug}` : '/api/saveTestimonialsSettings';

      const response = await axios.post(
        config.API_URL + endpoint,
        { settings: currentConfig },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 1) {
        alert("Testimonials saved successfully! 🎉");
        if (onSave) onSave(currentConfig);
      } else {
        alert("Save failed.");
      }
    } catch (error) {
      console.error("Save error:", error);
      alert("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  // Open modal
  const openAddTestimonialsModal = () => {
    setSearchQuery('');
    setSelectedTestimonialIds([]);
    setIsModalOpen(true);
  };

  // Toggle selection
  const toggleTestimonialSelection = (testimonialId) => {
    setSelectedTestimonialIds(prev =>
      prev.includes(testimonialId)
        ? prev.filter(id => id !== testimonialId)
        : [...prev, testimonialId]
    );
  };

  // Confirm add selected testimonials
  const confirmAddTestimonials = () => {
    const newTestimonials = allTestimonials
      .filter(t => selectedTestimonialIds.includes(t.id))
      .map(t => ({
        id: t.id,
        name: t.name,
        role: t.role,
        location: t.location,
        rating: t.rating,
        image: t.image || 'bg-gradient-to-br from-gray-100 to-gray-200',
        testimonial: t.testimonial,
        purchasedItem: t.purchasedItem,
        verified: t.verified || true,
      }));

    const existingIds = new Set(testimonials.map(t => t.id));
    const filteredNew = newTestimonials.filter(t => !existingIds.has(t.id));

    setTestimonials(prev => [...prev, ...filteredNew]);
    setIsModalOpen(false);
  };

  // Reorder & Remove
  const moveTestimonial = (id, direction) => {
    const index = testimonials.findIndex(t => t.id === id);
    if ((direction === -1 && index === 0) || (direction === 1 && index === testimonials.length - 1)) return;

    const newList = [...testimonials];
    [newList[index], newList[index + direction]] = [newList[index + direction], newList[index]];
    setTestimonials(newList);
  };

   const updateTestimonial = (id, field, value) => {
    setTestimonials(testimonials.map(t => 
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  const removeTestimonial = (id) => {
    setTestimonials(testimonials.filter(t => t.id !== id));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading testimonials settings...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Preview */}
      <div className="flex-1 relative">
        <div className="absolute inset-0 flex flex-col">
          <div className="bg-white border-b px-6 py-4 flex justify-between items-center shadow-sm">
            <h1 className="text-2xl font-bold text-gray-900">Testimonials Section Editor</h1>
            <button
              onClick={() => setFullScreenPreview(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700"
            >
              Full Screen Preview
            </button>
          </div>
          <div className="flex-1 overflow-auto bg-gray-50 p-8">
            <Testimonials config={currentConfig} />
          </div>
        </div>
      </div>

      {/* Settings Drawer */}
      <div className="w-96 bg-white border-l overflow-y-auto shadow-2xl relative">
        <div className="p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">Testimonials Settings</h2>

          {/* Title & Subtitle */}
          <div className="space-y-3">
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-4 py-2 border rounded-md" placeholder="Section Title" />
            <textarea value={subtitle} onChange={e => setSubtitle(e.target.value)} className="w-full px-4 py-2 border rounded-md h-20" placeholder="Subtitle" />
          </div>

          {/* Testimonials */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Selected Testimonials ({testimonials.length})</h3>
              <button
                onClick={openAddTestimonialsModal}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700"
              >
                + Add Testimonials
              </button>
            </div>

            {testimonials.map((t) => (
              <div key={t.id} className="border rounded-lg p-4 bg-gray-50 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{t.name}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => moveTestimonial(t.id, -1)}
                      disabled={testimonials.findIndex(item => item.id === t.id) === 0}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-50"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveTestimonial(t.id, 1)}
                      disabled={testimonials.findIndex(item => item.id === t.id) === testimonials.length - 1}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-50"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => removeTestimonial(t.id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      ×
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <input value={t.name} onChange={e => updateTestimonial(t.id, 'name', e.target.value)} placeholder="Name" className="px-3 py-2 border rounded-md" />
                  <input value={t.role} onChange={e => updateTestimonial(t.id, 'role', e.target.value)} placeholder="Role" className="px-3 py-2 border rounded-md" />
                </div>

                <input value={t.location} onChange={e => updateTestimonial(t.id, 'location', e.target.value)} placeholder="Location" className="w-full px-3 py-2 border rounded-md" />

                <textarea value={t.testimonial} onChange={e => updateTestimonial(t.id, 'testimonial', e.target.value)} placeholder="Testimonial text" className="w-full px-3 py-2 border rounded-md h-24" />

                <div className="grid grid-cols-2 gap-3">
                  <input value={t.purchasedItem} onChange={e => updateTestimonial(t.id, 'purchasedItem', e.target.value)} placeholder="Purchased Item" className="px-3 py-2 border rounded-md" />
                  <div className="flex items-center gap-2">
                    <label className="text-sm">Rating:</label>
                    <select value={t.rating} onChange={e => updateTestimonial(t.id, 'rating', Number(e.target.value))} className="px-3 py-2 border rounded-md">
                      {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} ★</option>)}
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={t.verified} onChange={e => updateTestimonial(t.id, 'verified', e.target.checked)} className="h-4 w-4 text-blue-600" />
                  <label className="text-sm">Verified Purchase</label>
                </div>

                <input value={t.image} onChange={e => updateTestimonial(t.id, 'image', e.target.value)} placeholder="Background gradient or image URL" className="w-full px-3 py-2 border rounded-md text-xs" />
              </div>
            ))}
          </div>

          {/* Dev Mode */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-yellow-900">Dev Mode – Custom Styles</h3>
              <button onClick={() => setDevMode(!devMode)} className={`relative inline-flex h-6 w-11 rounded-full transition ${devMode ? 'bg-yellow-600' : 'bg-gray-300'}`}>
                <span className={`inline-block h-4 w-4 rounded-full bg-white transition ${devMode ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {devMode && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Padding (Tailwind)</label>
                  <input value={container.padding} onChange={e => setContainer({...container, padding: e.target.value})} placeholder="py-12 md:py-20" className="w-full px-3 py-2 border rounded-md text-sm" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Background</label>
                  <input value={container.background} onChange={e => setContainer({...container, background: e.target.value})} placeholder="bg-gray-50 OR #f9fafb OR url(...)" className="w-full px-3 py-2 border rounded-md text-sm" />
                  <p className="text-xs text-gray-500 mt-1">Supports hex, gradient, image URL</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload Background Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      const formData = new FormData();
                      formData.append('image', file);

                      try {
                        const res = await axios.post(
                          `${config.API_URL}/api/upload-image`,
                          formData,
                          { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
                        );

                        if (res.data.url) {
                          setContainer(prev => ({ ...prev, background: `url(${res.data.url})` }));
                          alert("Background image uploaded!");
                        }
                      } catch (err) {
                        alert("Upload failed");
                      }
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title Color</label>
                    <input type="color" value={container.titleColor} onChange={e => setContainer({...container, titleColor: e.target.value})} className="w-full h-10 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle Color</label>
                    <input type="color" value={container.subtitleColor} onChange={e => setContainer({...container, subtitleColor: e.target.value})} className="w-full h-10 rounded" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                    <input type="color" value={container.textColor} onChange={e => setContainer({...container, textColor: e.target.value})} className="w-full h-10 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Star Color</label>
                    <input type="color" value={container.starColor} onChange={e => setContainer({...container, starColor: e.target.value})} className="w-full h-10 rounded" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Bg</label>
                  <input type="color" value={container.ctaBg} onChange={e => setContainer({...container, ctaBg: e.target.value})} className="w-full h-10 rounded" />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="fixed bottom-0 right-0 w-96 bg-white border-t p-6 shadow-lg">
          <button
            onClick={saveTestimonialsSettings}
            disabled={saving}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 disabled:opacity-70 shadow"
          >
            {saving ? 'Saving...' : 'Save & Publish Testimonials'}
          </button>
        </div>
      </div>

      {/* Add Testimonials Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto flex flex-col">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Add Testimonials</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-900 text-3xl">×</button>
            </div>

            <div className="p-6">
              {/* Search */}
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search testimonials..."
                className="w-full px-4 py-2 border rounded-md mb-6"
              />

              {/* Testimonials List */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {allTestimonials
                  .filter(t => searchQuery === '' || t.name.toLowerCase().includes(searchQuery.toLowerCase()) || t.testimonial.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map(t => (
                    <div
                      key={t.id}
                      className={`p-4 border rounded-lg cursor-pointer transition ${
                        selectedTestimonialIds.includes(t.id) ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => toggleTestimonialSelection(t.id)}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedTestimonialIds.includes(t.id)}
                          onChange={() => {}}
                          onClick={e => e.stopPropagation()}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{t.name}</p>
                          <p className="text-sm text-gray-600">{t.role} • {t.location}</p>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{t.testimonial}</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {allTestimonials.filter(t => searchQuery === '' || t.name.toLowerCase().includes(searchQuery.toLowerCase()) || t.testimonial.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
                <p className="text-center text-gray-500 mt-8">No testimonials found</p>
              )}
            </div>

            <div className="border-t px-6 py-4 flex justify-end gap-4 sticky bottom-0 bg-white">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddTestimonials}
                disabled={selectedTestimonialIds.length === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Add {selectedTestimonialIds.length} Testimonial{selectedTestimonialIds.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Preview */}
      {fullScreenPreview && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" onClick={() => setFullScreenPreview(false)}>
          <div className="bg-white/10 backdrop-blur px-6 py-4 flex justify-end">
            <button className="text-white text-4xl hover:text-gray-300">×</button>
          </div>
          <div className="flex-1 overflow-auto p-8" onClick={e => e.stopPropagation()}>
            <Testimonials config={currentConfig} />
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsAdmin;